// "Ver os lançamentos" de um fornecedor (01/10/2026). Existe para responder
// "posso apagar este?" ANTES de apagar: se há lançamento, o caminho é juntar
// com outro fornecedor, não excluir.
import { useEffect, useState } from 'react';
import axios from 'axios';
import { Modal, Table, Alert, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { API_URL } from '../../config';
import { getToken } from '../../utils/auth';
import { fmtDataBr } from '../../utils/dataFmt';
import { FornecedorLinha, fmtDinheiro, mensagemDeErro, paraNumero } from './logica';

const { Text } = Typography;

const LIMITE = 200;

interface Lancamento {
  id: string | number;
  data_competencia: string | null;
  data_vencimento: string | null;
  descricao: string | null;
  valor: string | number | null;   // numeric chega como string
  status: string | null;
  categoria_dre: string | null;
  centro_custo: string | null;
}

interface Resposta {
  fornecedor: { id: string; nome_fantasia: string };
  total: number;
  truncado: boolean;
  lancamentos: Lancamento[];
}

export default function ModalLancamentosFornecedor({ fornecedor, onFechar }: {
  fornecedor: FornecedorLinha;
  onFechar: () => void;
}) {
  const [dados, setDados] = useState<Resposta | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    let vivo = true;   // fechar o modal no meio da busca não pode atualizar estado morto
    (async () => {
      try {
        const token = await getToken();
        const r = await axios.get(
          `${API_URL}/financeiro/fornecedores/${encodeURIComponent(fornecedor.id)}/lancamentos?limit=${LIMITE}`,
          { headers: { Authorization: token } });
        const d = r.data.body ? JSON.parse(r.data.body) : r.data;
        if (vivo) setDados(d);
      } catch (e: any) {
        if (vivo) setErro(mensagemDeErro(e, 'Erro ao buscar os lançamentos'));
      } finally { if (vivo) setCarregando(false); }
    })();
    return () => { vivo = false; };
  }, [fornecedor.id]);

  const columns: ColumnsType<Lancamento> = [
    {
      title: 'Competência', dataIndex: 'data_competencia', width: 110,
      sorter: (a, b) => String(a.data_competencia || '').localeCompare(String(b.data_competencia || '')),
      defaultSortOrder: 'descend',
      render: v => fmtDataBr(v),
    },
    { title: 'Vencimento', dataIndex: 'data_vencimento', width: 110, render: v => fmtDataBr(v) },
    { title: 'Descrição', dataIndex: 'descricao', render: v => v || '—' },
    {
      title: 'Valor', dataIndex: 'valor', width: 120, align: 'right',
      sorter: (a, b) => Math.abs(paraNumero(a.valor)) - Math.abs(paraNumero(b.valor)),
      render: v => fmtDinheiro(Math.abs(paraNumero(v))),
    },
    { title: 'Situação', dataIndex: 'status', width: 100, render: v => v ? <Tag>{v}</Tag> : '—' },
    { title: 'Categoria', dataIndex: 'categoria_dre', render: v => v || '—' },
    { title: 'Centro de custo', dataIndex: 'centro_custo', render: v => v || '—' },
  ];

  const total = dados?.total ?? 0;

  return (
    <Modal open onCancel={onFechar} footer={null} destroyOnClose centered
      title={`Lançamentos de ${fornecedor.nome_fantasia}`}
      width="94vw" style={{ maxWidth: 1100 }}
      styles={{ body: { maxHeight: 'calc(100vh - 130px)', overflowY: 'auto' } }}>
      {erro && <Alert type="error" showIcon message="Não foi possível carregar" description={erro} />}
      {dados && total === 0 && (
        <Alert type="success" showIcon style={{ marginBottom: 12 }}
          message="Este fornecedor não tem nenhum lançamento"
          description="Pode ser excluído sem afetar o Contas a Pagar." />
      )}
      {dados && total > 0 && (
        <Alert type="info" showIcon style={{ marginBottom: 12 }}
          message={`${total} lançamento(s) neste fornecedor`}
          description={<>
            Fornecedor com lançamento não pode ser excluído — para tirá-lo da lista, use <b>Juntar</b> com o cadastro certo.
            {dados.truncado && <> Mostrando só os {dados.lancamentos.length} mais recentes.</>}
          </>} />
      )}
      {(carregando || (dados && total > 0)) && (
        <Table rowKey="id" size="small" columns={columns} loading={carregando}
          dataSource={dados?.lancamentos || []}
          pagination={{ pageSize: 50, hideOnSinglePage: true, showSizeChanger: false }}
          scroll={{ x: 'max-content' }} />
      )}
      {!carregando && !dados && !erro && <Text type="secondary">Sem dados.</Text>}
    </Modal>
  );
}
